// SPDX-License-Identifier: MIT
// DownloadManager - orchestrates queued downloads with concurrency + integrity checks (Phase 4 scaffold)

import { ConcurrencyController } from './concurrency-controller';
import { IntegrityGuard } from './integrity-guard';
import {
  DownloadRequest,
  DownloadTask,
  DownloadResultSuccess,
  ProgressSnapshot,
  ConcurrencyConfig,
  IntegrityIssue,
  ClassifiedError,
  DownloadManagerSummary,
} from './types';

interface DownloadTaskWithBlob extends DownloadTask {
  blob?: Blob;
}
import { getSasUrl } from '../auth/session';
import { ContainerClient } from '@azure/storage-blob';
import { ErrorClassifier } from '../errors/error-classifier';
import { RetryHandler } from '../errors/retry-handler';
import { DebugLogger } from '../observability/debug-logger';
import { UserFeedbackDispatcher, UserMessage } from '../observability/user-feedback-dispatcher';

export interface DownloadManagerOptions {
  concurrency?: number; // default 3
  disableIntegrity?: boolean; // skip integrity guard (testing / perf)
  maxAttempts?: number; // retry attempts for transient errors (default 3)
}

export class DownloadManager {
  private tasks = new Map<string, DownloadTask>();
  private order: string[] = [];
  private pool: ConcurrencyController;
  private guard: IntegrityGuard | null;
  private containerClient: ContainerClient;
  private listeners: Array<(snapshot: ProgressSnapshot) => void> = [];
  private issues: IntegrityIssue[] = [];
  private pending: Promise<unknown>[] = [];
  private classifier = new ErrorClassifier();
  private retryHandler: RetryHandler;
  private logger = new DebugLogger();
  private feedback = new UserFeedbackDispatcher();
  private expiresAt?: number; // SAS expiry timestamp (ms since epoch)
  private notifiedExpired = false;
  // Keep in sync with SAFE_PREFIX_RE used in lister & UI; deny traversal or invalid chars
  private static readonly SAFE_PATH_RE = /^[A-Za-z0-9_\-/\.]*$/;

  private static isSafePath(p: string): boolean {
    if (!p) return false;
    if (p.startsWith('/') || p.startsWith('\\')) return false;
    if (p.includes('..')) return false;
    return DownloadManager.SAFE_PATH_RE.test(p);
  }

  private isExpiredNow(skewMs = 5000): boolean {
    return typeof this.expiresAt === 'number' && Date.now() > this.expiresAt - Math.max(0, skewMs);
  }

  private notifyExpiredOnce() {
    if (this.notifiedExpired) return;
    this.notifiedExpired = true;
    try {
      // Avoid leaking details; concise user-facing message
      (this.feedback as any).emit?.({
        type: 'error',
        message: 'Your Azure link has expired. Please re-authenticate with a fresh SAS.',
      });
    } catch {
      /* no-op */
    }
  }

  constructor(opts: DownloadManagerOptions = {}) {
    const sas = getSasUrl();
    if (!sas) throw new Error('NOT_AUTHENTICATED');
    // Normalize to blob container base URL even if session stored a dfs host or directory-scoped URL
    try {
      const u = new URL(sas);
      // Record SAS expiry if present (fail fast when expired)
      const se = u.searchParams.get('se');
      if (se) {
        const t = Date.parse(se);
        if (!Number.isNaN(t)) this.expiresAt = t;
      }
      const host = u.hostname.replace('.dfs.core.', '.blob.core.');
      const segs = u.pathname.split('/').filter(Boolean);
      const container = segs[0] || '';
      const normalized = container
        ? `https://${host}/${container}?${u.searchParams.toString()}`
        : `https://${host}${u.pathname}?${u.searchParams.toString()}`;
      this.containerClient = new ContainerClient(normalized);
    } catch {
      // fallback to raw
      this.containerClient = new ContainerClient(sas);
    }
    this.pool = new ConcurrencyController({
      maxParallel: opts.concurrency ?? 3,
    } as ConcurrencyConfig);
    this.guard = opts.disableIntegrity ? null : new IntegrityGuard();
    this.retryHandler = new RetryHandler({ maxAttempts: opts.maxAttempts ?? 3 });
    this.logger.log('download-manager:init', {
      concurrency: opts.concurrency,
      maxAttempts: opts.maxAttempts,
    });
  }

  /** Dynamically enable or disable debug logging. */
  setDebugEnabled(flag: boolean) {
    (this.logger as any).setEnabled?.(flag);
  }

  enqueue(requests: DownloadRequest[]): string[] {
    const ids: string[] = [];
    const mock = (import.meta as any).env?.VITE_E2E_MOCK === '1';
    for (const r of requests) {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const task: DownloadTask = {
        id,
        path: r.path,
        size: r.size,
        contentType: r.contentType,
        state: 'queued',
        progressBytes: 0,
      };
      this.tasks.set(id, task);
      this.order.push(id);
      ids.push(id);

      // Validate path defensively before any work
      if (!DownloadManager.isSafePath(r.path)) {
        task.state = 'failed';
        task.errorCategory = 'input-invalid' as any;
        continue;
      }
      // Fail fast when SAS is expired
      if (this.isExpiredNow()) {
        task.state = 'failed';
        task.errorCategory = 'auth-expired' as any;
        this.notifyExpiredOnce();
        continue;
      }

      if (mock) {
        // Immediate success mock with format-aware synthetic content
        task.startedAt = Date.now();
        let blob: Blob;
        const lower = r.path.toLowerCase();
        if (lower.endsWith('.geojson')) {
          const fc = { type: 'FeatureCollection', features: [] } as const;
          blob = new Blob([JSON.stringify(fc)], { type: 'application/geo+json' });
          task.contentType = 'application/geo+json';
        } else if (lower.endsWith('.json')) {
          blob = new Blob([JSON.stringify({ mock: true })], { type: 'application/json' });
          task.contentType = 'application/json';
        } else if (
          lower.endsWith('.parquet') ||
          lower.endsWith('.geoparquet') ||
          lower.endsWith('.pq') ||
          lower.endsWith('.parq')
        ) {
          // Parquet binary is heavy; use CSV in mock and adapter handles renaming
          const name = r.path.split('/').pop() || 'mock.parquet';
          blob = new Blob([`id,value\n1,${name}`], { type: 'text/csv' });
          task.contentType = 'text/csv';
        } else if (lower.endsWith('.csv')) {
          const name = r.path.split('/').pop();
          blob = new Blob([`colA,colB\n1,${name}`], { type: 'text/csv' });
          task.contentType = 'text/csv';
        } else {
          blob = new Blob([`name,path\n${r.path.split('/').pop()},${r.path}`], {
            type: 'text/plain',
          });
        }
        (task as DownloadTaskWithBlob).blob = blob;
        task.state = 'succeeded';
        task.completedAt = Date.now();
        // Emit completion snapshot asynchronously to simulate event loop delay
        setTimeout(() => this.emit(), 10);
        continue;
      }

      // Real mode: schedule the task
      this.schedule(task);
    }
    this.emit();
    return ids;
  }

  cancel(id: string) {
    const t = this.tasks.get(id);
    if (t && (t.state === 'queued' || t.state === 'running')) {
      t.state = 'cancelled';
      t._controller?.abort();
      this.emit();
    }
  }

  subscribe(fn: (snap: ProgressSnapshot) => void) {
    this.listeners.push(fn);
    fn(this.snapshot());
    return () => {
      this.listeners = this.listeners.filter(l => l !== fn);
    };
  }

  private schedule(task: DownloadTask) {
    if (task.state !== 'queued') return;
    // Prevent scheduling when expired
    if (this.isExpiredNow()) {
      task.state = 'failed';
      task.errorCategory = 'auth-expired' as any;
      this.notifyExpiredOnce();
      this.emit();
      return;
    }
    const p = this.pool
      .run(() => this.runTaskWithRetry(task))
      .catch(() => {
        /* error already recorded */
      });
    this.pending.push(p);
  }

  /** Expose subscription to user feedback dispatcher messages. */
  subscribeMessages(fn: (messages: UserMessage[]) => void) {
    return this.feedback.subscribe(fn);
  }

  private async runTaskWithRetry(task: DownloadTask) {
    task.attempt = (task.attempt ?? 0) + 1;
    if (!DownloadManager.isSafePath(task.path)) {
      task.state = 'failed';
      task.errorCategory = 'input-invalid' as any;
      this.emit();
      return;
    }
    if (this.isExpiredNow()) {
      task.state = 'failed';
      task.errorCategory = 'auth-expired' as any;
      this.notifyExpiredOnce();
      this.emit();
      return;
    }
    try {
      await this.runTask(task);
    } catch (raw) {
      const classified = this.classifier.classify(raw);
      task.errorCategory = classified.category;
      const attempt = task.attempt || 1;
      const canRetry = this.retryHandler.shouldRetry(classified, attempt);
      this.logger.error('download-task:error', classified, { path: task.path, attempt });
      if (canRetry && task.state !== 'cancelled') {
        task.state = 'queued';
        const delayAttempt = attempt + 1;
        try {
          await this.retryHandler.waitBeforeRetry(delayAttempt, classified.category);
          this.schedule(task);
        } catch {
          // aborted waiting
        }
      } else {
        if (!classified.retryable || !canRetry) {
          this.feedback.errorFromClassification(classified);
        }
      }
    }
  }

  private async runTask(task: DownloadTask) {
    if (task.state !== 'queued') return;
    task.state = 'running';
    task.startedAt = Date.now();
    const controller = new AbortController();
    task._controller = controller;
    this.emit();
    let cancelled = false;
    try {
      const blobClient = this.containerClient.getBlobClient(task.path);
      this.logger.log('download:begin', { path: task.path, attempt: task.attempt });
      const resp = await blobClient.download(0, undefined, { abortSignal: controller.signal });
      const blob: Blob = await resp.blobBody!; // azure SDK provides blobBody
      (task as DownloadTaskWithBlob).blob = blob; // store for later ingestion
      if (controller.signal.aborted) {
        cancelled = true;
      }
      if (!cancelled) {
        if (this.guard) {
          const integrity = await this.guard.validate(task, blob);
          if (!integrity.ok) {
            task.state = 'failed';
            task.error = integrity.issues.map(i => i.message).join('; ');
            this.issues.push(...integrity.issues);
            const classified: ClassifiedError = {
              category: 'integrity',
              message: task.error,
              retryable: false,
            };
            task.errorCategory = 'integrity';
            this.logger.error('download:integrity-fail', classified, { path: task.path });
          } else {
            task.state = 'succeeded';
            task.completedAt = Date.now();
            const durationMs = (task.completedAt ?? Date.now()) - (task.startedAt ?? Date.now());
            this.logger.log('download:success', {
              path: task.path,
              bytes: blob.size,
              attempt: task.attempt,
              durationMs,
            });
          }
        } else {
          task.state = 'succeeded';
          task.completedAt = Date.now();
          const durationMs = (task.completedAt ?? Date.now()) - (task.startedAt ?? Date.now());
          this.logger.log('download:success', {
            path: task.path,
            bytes: blob.size,
            attempt: task.attempt,
            integrityDisabled: true,
            durationMs,
          });
        }
      } else {
        task.state = 'cancelled';
        task.errorCategory = 'cancelled';
        this.logger.log('download:cancelled', { path: task.path });
      }
    } catch (e) {
      if (controller.signal.aborted) {
        task.state = 'cancelled';
        task.errorCategory = 'cancelled';
      } else {
        task.state = 'failed';
        task.error = (e as Error).message;
        const classified = this.classifier.classify(e);
        task.errorCategory = classified.category;
        // Re-throw so retry wrapper can process
        throw classified;
      }
    } finally {
      this.emit();
    }
  }

  getResults(): DownloadResultSuccess[] {
    const results: DownloadResultSuccess[] = [];
    for (const id of this.order) {
      const t = this.tasks.get(id) as DownloadTaskWithBlob | undefined;
      if (t && t.state === 'succeeded' && t.blob) {
        results.push({
          path: t.path,
          bytes: t.blob.size,
          blob: t.blob,
          integrity: { ok: true, issues: [] },
        });
      }
    }
    return results;
  }

  getTask(id: string): DownloadTask | undefined {
    return this.tasks.get(id);
  }

  listTasks(): DownloadTask[] {
    return this.order.map(id => this.tasks.get(id)!).filter(Boolean);
  }

  private snapshot(): ProgressSnapshot {
    let running = 0,
      queued = 0,
      completed = 0,
      failed = 0,
      cancelled = 0;
    let bytesCompleted = 0;
    let bytesTotal = 0;
    const total = this.order.length;
    for (const id of this.order) {
      const t = this.tasks.get(id)!;
      if (t.size) bytesTotal += t.size;
      switch (t.state) {
        case 'queued':
          queued++;
          break;
        case 'running':
          running++;
          break;
        case 'succeeded':
          completed++;
          if (t.size) bytesCompleted += t.size;
          break;
        case 'failed':
          failed++;
          break;
        case 'cancelled':
          cancelled++;
          break;
      }
    }
    return {
      total,
      running,
      queued,
      completed,
      failed,
      cancelled,
      bytesCompleted,
      bytesTotal: bytesTotal || undefined,
      updatedAt: Date.now(),
    };
  }

  private emit() {
    const snap = this.snapshot();
    this.listeners.forEach(l => l(snap));
  }

  getIssues(): IntegrityIssue[] {
    return [...this.issues];
  }

  summary(): DownloadManagerSummary {
    const snap = this.snapshot();
    const byCategory: Record<string, number> = {};
    let retryAttempts = 0;
    let maxAttemptObserved = 0;
    let firstFailureAt: number | undefined;
    let lastFailureAt: number | undefined;
    for (const t of this.listTasks()) {
      if (t.errorCategory) {
        byCategory[t.errorCategory] = (byCategory[t.errorCategory] || 0) + 1;
        if (t.completedAt) {
          if (!firstFailureAt || t.completedAt < firstFailureAt) firstFailureAt = t.completedAt;
          if (!lastFailureAt || t.completedAt > lastFailureAt) lastFailureAt = t.completedAt;
        }
      }
      if (t.attempt && t.attempt > 1) retryAttempts += t.attempt - 1;
      if (t.attempt && t.attempt > maxAttemptObserved) maxAttemptObserved = t.attempt;
    }
    const byState: Record<any, number> = {
      queued: 0,
      running: 0,
      succeeded: 0,
      failed: 0,
      cancelled: 0,
    };
    for (const t of this.listTasks()) {
      byState[t.state] = (byState[t.state] || 0) + 1;
    }
    return {
      total: snap.total,
      succeeded: snap.completed,
      failed: snap.failed,
      cancelled: snap.cancelled,
      issues: this.getIssues(),
      byState: byState as Record<any, number>,
      errors: { byCategory, retryAttempts, maxAttemptObserved, firstFailureAt, lastFailureAt },
    };
  }

  /** Await until all currently enqueued tasks have reached a terminal state. */
  async awaitIdle(timeoutMs = 5000): Promise<void> {
    const start = Date.now();
    while (this.pending.length) {
      const batch = [...this.pending];
      this.pending = [];
      await Promise.race([
        Promise.allSettled(batch),
        new Promise((_, rej) =>
          setTimeout(() => rej(new Error('awaitIdle timeout')), timeoutMs - (Date.now() - start))
        ),
      ]);
      if (Date.now() - start > timeoutMs) throw new Error('awaitIdle timeout');
    }
  }
}
